import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = '/api';

const tiers = [
  {
    id: "entry",
    name: "Entry",
    price: 249,
    colour: "#364225",
    features: [
      "Access to T.I.I.N.A. network events",
      "Quarterly newsletter and updates",
      "Community resource library",
      "Online member directory listing",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    price: 2000,
    colour: "#bb321a",
    features: [
      "All Entry benefits",
      "Priority access to training programmes",
      "Annual strategy workshop invitation",
      "Mentorship pairing with senior leaders",
      "Governance toolkit access",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    price: 10000,
    colour: "#364225",
    popular: true,
    features: [
      "All Growth benefits",
      "Dedicated account manager",
      "Custom research and policy briefs",
      "Board governance advisory sessions",
      "Priority procurement opportunities",
      "Bespoke digital tool consultation",
    ],
  },
  {
    id: "international",
    name: "International",
    price: null,
    inviteOnly: true,
    colour: "#bb321a",
    features: [
      "All Premium benefits",
      "Global Indigenous network access",
      "International partnership facilitation",
      "Cross-border economic development support",
      "Dedicated innovation lab access",
      "Executive leadership programme",
      "Climate adaptation strategy consulting",
    ],
  },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 0 }).format(n);

export default function MembershipSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [showTiers, setShowTiers] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    company_description: "",
    payment_method: "invoice",
    region: "",
    industry: "",
    employee_count: "",
    additional_notes: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.company_description) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const tier = tiers.find(t => t.id === selectedTier);
      const res = await axios.post(`${API}/membership-enquiry`, {
        tier_id: selectedTier,
        tier_name: tier?.name || selectedTier,
        ...form,
      });

      // Credit/Debit Card: redirect to Stripe checkout
      if (form.payment_method === "credit_card" && res.data.checkout_url) {
        toast.success("Redirecting to secure payment...");
        window.location.href = res.data.checkout_url;
        return;
      }

      setSubmitted(true);
      toast.success(
        form.payment_method === "invoice"
          ? "Thank you! Your invoice has been generated and will be sent shortly."
          : form.payment_method === "direct_debit"
          ? "Thank you! Please make payment using the bank details provided."
          : "Thank you! Your membership enquiry has been received."
      );
    } catch (err) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative py-8 sm:py-12 overflow-hidden"
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">
            Membership
          </p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05] mb-6">
            Join the <span className="text-white/60">network.</span>
          </h2>
          <p className="text-white/90 text-base sm:text-lg max-w-2xl mx-auto mb-8">
            The most powerful investments of the next decade will be made alongside Indigenous communities, not despite them. Join a network of leaders, enterprises, and organisations who understand that.
          </p>

          {!showTiers && !submitted && (
            <div className="flex flex-col items-center gap-4">
              <a
                href="/register"
                data-testid="join-network-btn"
                className="px-10 py-4 text-white font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-all hover:scale-105 inline-block"
                style={{ background: "linear-gradient(135deg, #bb321a, #485e23, #364225)" }}
              >
                Register Now
              </a>
              <div className="mt-4 flex flex-col items-center gap-2">
                <img src="/tiina-register-qr.png" alt="Scan to register" className="w-28 h-28" />
                <p className="text-white/40 text-[10px] uppercase tracking-wider">Scan to register</p>
              </div>
              <a href="/auth?type=member" className="text-white/60 text-sm hover:text-white/90 transition-colors" data-testid="membership-portal-link">
                Already a member? Sign in
              </a>
            </div>
          )}

          {showTiers && !selectedTier && !submitted && (
            <p className="text-sm">
              <a href="/auth?type=member" className="text-white/60 hover:text-white/90 transition-colors">
                Already a member? Sign in
              </a>
            </p>
          )}
        </motion.div>

        {/* Tier cards - only shown after clicking Join */}
        {showTiers && !submitted && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1 mb-16">
          {tiers.map((tier, i) => {
            const isSelected = selectedTier === tier.id;
            return (
              <motion.div
                key={tier.id}
                data-testid={`membership-${tier.id}`}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className={`relative bg-white/10 backdrop-blur-sm border flex flex-col transition-all duration-200 ${
                  isSelected
                    ? "border-[#bb321a] ring-2 ring-[#bb321a]"
                    : tier.popular
                      ? "border-[#bb321a] ring-1 ring-[#bb321a]"
                      : "border-white/20"
                }`}
              >
                {tier.popular && !selectedTier && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#bb321a] text-white text-xs font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                {isSelected && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#bb321a] text-white text-xs font-bold uppercase tracking-wider">
                    Selected
                  </div>
                )}
                <div className="p-6 sm:p-8 flex-1 flex flex-col">
                  <h3 className="font-['Syne'] text-xl font-bold text-white mb-1">
                    {tier.name}
                  </h3>
                  <div className="mb-6">
                    {tier.inviteOnly ? (
                      <span className="font-['Syne'] text-xl sm:text-2xl font-bold text-white">Invitation Only</span>
                    ) : (
                      <>
                        <span className="font-['Syne'] text-3xl sm:text-4xl font-black" style={{ color: tier.colour }}>
                          {fmt(tier.price)}
                        </span>
                        <span className="text-white/90 text-sm">/year</span>
                      </>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-white/90">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: tier.colour }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`select-${tier.id}`}
                    onClick={() => setSelectedTier(isSelected ? null : tier.id)}
                    className="w-full py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-all duration-200 hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, #bb321a, #485e23, #364225)` }}
                  >
                    {isSelected ? "Selected" : "Get Started"}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
        )}

        {/* Enquiry form - appears when tier selected */}
        {showTiers && selectedTier && !submitted && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto bg-white border border-white/20 p-8 sm:p-10"
            data-testid="membership-enquiry-form"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-['Syne'] text-2xl font-bold text-white">
                  {tiers.find(t => t.id === selectedTier)?.name} Membership
                </h3>
                <p className="text-white/90 text-sm mt-1">
                  Complete your details below and we will be in touch{form.payment_method === "direct_debit" ? ". Our bank details are shown below for direct payment." : form.payment_method === "credit_card" ? ". We will send you a secure payment link." : " with your invoice."} 
                </p>
              </div>
              <button onClick={() => setSelectedTier(null)} className="text-white/90 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Contact Name *</label>
                  <Input
                    data-testid="enquiry-contact-name"
                    value={form.contact_name}
                    onChange={e => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Your full name"
                    className="rounded-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Email *</label>
                  <Input
                    data-testid="enquiry-email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="your@company.co.nz"
                    className="rounded-none"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Company / Organisation Name *</label>
                  <Input
                    data-testid="enquiry-company"
                    value={form.company_name}
                    onChange={e => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Your organisation"
                    className="rounded-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Phone</label>
                  <Input
                    data-testid="enquiry-phone"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+64 21 xxx xxxx"
                    className="rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Website</label>
                <Input
                  data-testid="enquiry-website"
                  value={form.website}
                  onChange={e => setForm({ ...form, website: e.target.value })}
                  placeholder="www.yourcompany.co.nz"
                  className="rounded-none"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Region</label>
                  <select
                    data-testid="enquiry-region"
                    value={form.region}
                    onChange={e => setForm({ ...form, region: e.target.value })}
                    className="w-full h-10 border border-white/20 px-3 text-sm"
                  >
                    <option value="">Select region</option>
                    <option value="Te Tai Tokerau / Northland">Te Tai Tokerau / Northland</option>
                    <option value="Tamaki Makaurau / Auckland">Tamaki Makaurau / Auckland</option>
                    <option value="Waikato">Waikato</option>
                    <option value="Te Moana a Toi / Bay of Plenty">Te Moana a Toi / Bay of Plenty</option>
                    <option value="Tairawhiti / Gisborne">Tairawhiti / Gisborne</option>
                    <option value="Hawke's Bay">Hawke's Bay</option>
                    <option value="Taranaki">Taranaki</option>
                    <option value="Manawatu-Whanganui">Manawatu-Whanganui</option>
                    <option value="Te Whanganui-a-Tara / Wellington">Te Whanganui-a-Tara / Wellington</option>
                    <option value="Te Waipounamu / South Island">Te Waipounamu / South Island</option>
                    <option value="Pacific Islands">Pacific Islands</option>
                    <option value="Australia">Australia</option>
                    <option value="International">International</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Industry</label>
                  <select
                    data-testid="enquiry-industry"
                    value={form.industry}
                    onChange={e => setForm({ ...form, industry: e.target.value })}
                    className="w-full h-10 border border-white/20 px-3 text-sm"
                  >
                    <option value="">Select industry</option>
                    <option value="Iwi / Hapu / Trust">Iwi / Hapu / Trust</option>
                    <option value="Government / Public Sector">Government / Public Sector</option>
                    <option value="Health & Social Services">Health & Social Services</option>
                    <option value="Education & Training">Education & Training</option>
                    <option value="Primary Industries">Primary Industries</option>
                    <option value="Construction & Infrastructure">Construction & Infrastructure</option>
                    <option value="Technology & Digital">Technology & Digital</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Tourism & Hospitality">Tourism & Hospitality</option>
                    <option value="Not-for-Profit">Not-for-Profit</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Employees</label>
                  <select
                    data-testid="enquiry-employees"
                    value={form.employee_count}
                    onChange={e => setForm({ ...form, employee_count: e.target.value })}
                    className="w-full h-10 border border-white/20 px-3 text-sm"
                  >
                    <option value="">Select size</option>
                    <option value="1-5">1-5</option>
                    <option value="6-20">6-20</option>
                    <option value="21-50">21-50</option>
                    <option value="51-100">51-100</option>
                    <option value="101-500">101-500</option>
                    <option value="500+">500+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">About Your Organisation *</label>
                <Textarea
                  data-testid="enquiry-description"
                  value={form.company_description}
                  onChange={e => setForm({ ...form, company_description: e.target.value })}
                  placeholder="Tell us about your organisation, what you do, and your goals..."
                  rows={4}
                  className="rounded-none resize-none"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-2 block">Preferred Payment Method *</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "invoice", label: "Invoice" },
                    { value: "direct_debit", label: "Direct Debit" },
                    { value: "credit_card", label: "Credit/Debit Card" },
                  ].map(pm => (
                    <button
                      key={pm.value}
                      type="button"
                      data-testid={`payment-${pm.value}`}
                      onClick={() => setForm({ ...form, payment_method: pm.value })}
                      className={`px-5 py-3 text-sm font-bold border-2 transition-colors ${
                        form.payment_method === pm.value
                          ? "bg-[#bb321a] text-white border-[#bb321a]"
                          : "bg-transparent text-[#bb321a] border-[#45443f] hover:border-[#bb321a] hover:text-[#bb321a]"
                      }`}
                    >
                      {pm.label}
                    </button>
                  ))}
                </div>
                {form.payment_method === "direct_debit" && (
                  <div className="mt-3 p-5 bg-[#45443f] border border-white/20 text-sm">
                    <p className="font-bold text-white text-base mb-4">T.I.I.N.A. Bank Details</p>
                    <div className="space-y-2">
                      <p className="text-white/80">Bank: <strong className="text-white">BNZ (Bank of New Zealand)</strong></p>
                      <p className="text-white/80">Account Name: <strong className="text-white">T.I.I.N.A.</strong></p>
                      <p className="text-white/80">Account Number: <strong className="text-white">02-0412-0285700-000</strong></p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="font-bold text-white mb-2">Payment Reference (required)</p>
                      <p className="text-white/80">Reference: <strong className="text-white">{form.company_name || "[Your Business Name]"}</strong></p>
                      <p className="text-white/80">Particulars: <strong className="text-white">{selectedTier ? tiers.find(t => t.id === selectedTier)?.name : "[Tier]"} Membership</strong></p>
                      <p className="text-white/80">Code: <strong className="text-white">{form.contact_name || "[Contact Person]"}</strong></p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-white/80">Amount: <strong className="text-white text-lg">
                        {selectedTier && tiers.find(t => t.id === selectedTier)?.price
                          ? `$${(tiers.find(t => t.id === selectedTier).price * 1.15).toLocaleString("en-NZ", {minimumFractionDigits: 2})} NZD (incl. 15% GST)`
                          : "See tier pricing above"}
                      </strong></p>
                      <p className="text-white/60 text-xs mt-1">GST Number: 148-307-303</p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold tracking-wider uppercase text-white/90 mb-1 block">Additional Notes</label>
                <Textarea
                  data-testid="enquiry-notes"
                  value={form.additional_notes}
                  onChange={e => setForm({ ...form, additional_notes: e.target.value })}
                  placeholder="Anything else you would like us to know..."
                  rows={2}
                  className="rounded-none resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="enquiry-submit"
                disabled={loading}
                className="w-full py-4 text-white font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-all disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #bb321a, #485e23, #364225)" }}
              >
                {loading ? "Submitting..." : "Submit Membership Enquiry"}
              </button>
            </form>
          </motion.div>
        )}

        {/* Success confirmation */}
        {submitted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto text-center bg-white/10 backdrop-blur-sm border border-white/30 p-10"
            data-testid="enquiry-success"
          >
            <div className="w-16 h-16 bg-[#364225] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-['Syne'] text-2xl font-bold text-white mb-3">
              Thank you for your interest
            </h3>
            <p className="text-white/90 text-base leading-relaxed mb-6">
              Your membership enquiry has been received. Our team will review your details and send an invoice to the email address you provided. We look forward to welcoming you to the T.I.I.N.A. network.
            </p>
            <button
              onClick={() => { setSubmitted(false); setSelectedTier(null); setForm({ company_name: "", contact_name: "", email: "", phone: "", website: "", company_description: "", payment_method: "invoice", region: "", industry: "", employee_count: "", additional_notes: "" }); }}
              className="text-white font-semibold text-sm hover:underline"
            >
              Submit another enquiry
            </button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
